import React from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { StepComponentProps } from "../types/proposalForm.types";

const Step8Declaration: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const renderDateInput = (
    name: keyof typeof values,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="text"
          name={name as string}
          value={String(values[name] || "")}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!(touched[name] && errors[name])}
          placeholder={placeholder}
          className="form-control"
          id={`date-${name}`}
          onFocus={(e) => {
            e.target.type = "date";
          }}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={() => {
            const input = document.getElementById(`date-${name}`) as HTMLInputElement;
            if (input) {
              input.type = "date";
              if (input.showPicker) {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  return (
    <>
      {/* Legal Declaration Section */}
      <Card className="form-card shadow-sm border-0">
        <CardBody>
          <h5 className="form-title">Legal Declaration</h5>
          <p className="form-subtitle">
            Please read and agree to the following declarations
          </p>

          {/* Terms and Conditions Section */}
          <div className="declaration-section">
            <h6 className="declaration-section-title">Terms and Conditions</h6>
            <div className="declaration-content">
              <ol className="declaration-list">
                <li>
                  <strong>Declaration of Health and Accuracy:</strong> I declare that all statements made in this application are true and accurate to the best of my knowledge. I understand that any false or misleading information may result in the rejection of my claim or cancellation of the policy.
                </li>
                <li>
                  <strong>Medical Examination:</strong> I agree to undergo any necessary medical examinations as required by the insurance company at their expense. I authorize the release of my medical records to the insurance company for the purpose of underwriting and claims processing.
                </li>
                <li>
                  <strong>Premium Payment:</strong> I understand that the policy will commence only after the first premium is paid and the proposal is accepted by the insurance company. I agree to pay all subsequent premiums on their due dates to keep the policy in force.
                </li>
                <li>
                  <strong>Right to Cancellation:</strong> I understand that I have the right to cancel this policy within 30 days of receiving the policy document (free-look period) if I am not satisfied with the terms and conditions.
                </li>
                <li>
                  <strong>Data Privacy and Usage:</strong> I consent to the collection, processing, and storage of my personal information for the purpose of policy administration, claims processing, and regulatory compliance. I authorize the insurance company to share my information with reinsurers, regulatory authorities, and service providers as necessary.
                </li>
                <li>
                  <strong>Anti-Money Laundering (AML) Compliance:</strong> I declare that the source of funds used for premium payments is legitimate and obtained through lawful means. I agree to comply with all applicable anti-money laundering and counter-terrorism financing regulations.
                </li>
                <li>
                  <strong>Governing Law:</strong> I understand that this contract is governed by the laws of the jurisdiction where the policy is issued, and any disputes will be subject to the exclusive jurisdiction of the courts of that jurisdiction.
                </li>
              </ol>
              <div className="declaration-checkbox">
                <FormGroup check className="form-check">
                  <Input
                    type="checkbox"
                    id="declartion_accepted"
                    name="declartion_accepted"
                    checked={values.declartion_accepted === 1}
                    onChange={(e) =>
                      setFieldValue("declartion_accepted", e.target.checked ? 1 : 0)
                    }
                    className="form-check-input"
                  />
                  <Label check htmlFor="declartion_accepted" className="form-check-label">
                    I have read and agree to all the terms and conditions stated above.
                  </Label>
                </FormGroup>
              </div>
            </div>
          </div>

          {/* CRS / FATCA Declaration Section */}
          <div className="declaration-section">
            <h6 className="declaration-section-title">CRS / FATCA Declaration</h6>
            <div className="declaration-content">
              <div className="tax-residency-info">
                <h6 className="tax-residency-title">Tax Residency Information</h6>
                <p className="tax-residency-text">
                  As per FATCA (Foreign Account Tax Compliance Act) and CRS (Common Reporting Standard) regulations, we are required to collect information about your tax residency status.
                </p>
              </div>
              <div className="declaration-checkboxes">
                <FormGroup check className="form-check">
                  <Input
                    type="checkbox"
                    id="notfatca_holding"
                    name="notfatca_holding"
                    checked={values.notfatca_holding === 1}
                    onChange={(e) =>
                      setFieldValue("notfatca_holding", e.target.checked ? 1 : 0)
                    }
                    className="form-check-input"
                  />
                  <Label check htmlFor="notfatca_holding" className="form-check-label">
                    I confirm that I am not a US person for tax purposes and I am not subject to FATCA withholding. I understand that I must inform the company if my tax residency status changes.
                  </Label>
                </FormGroup>
                <FormGroup check className="form-check">
                  <Input
                    type="checkbox"
                    id="isverify_consent"
                    name="isverify_consent"
                    checked={values.isverify_consent === 1}
                    onChange={(e) =>
                      setFieldValue("isverify_consent", e.target.checked ? 1 : 0)
                    }
                    className="form-check-input"
                  />
                  <Label check htmlFor="isverify_consent" className="form-check-label">
                    I authorize the insurance company to verify the information provided and conduct necessary background checks. I consent to communication via email, SMS, and phone for policy-related matters.
                  </Label>
                </FormGroup>
              </div>
            </div>
          </div>

          {/* Date of Signature & Place */}
          <div className="declaration-section">
            <Row>
              <Col md={6}>
                {renderDateInput(
                  "date_of_sign",
                  "Date of Signature",
                  "DD/MM/YYYY (Auto filled)",
                  false
                )}
              </Col>
              <Col md={6}>
                <FormGroup className="form-group">
                  <Label>Place</Label>
                  <Input
                    type="text"
                    name="placeof_sign"
                    value={String(values.placeof_sign || "")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={!!(touched.placeof_sign && errors.placeof_sign)}
                    placeholder="Auto filled"
                    className="form-control"
                  />
                  {touched.placeof_sign && errors.placeof_sign && (
                    <div className="invalid-feedback">{errors.placeof_sign}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </div>

          {/* Final Reminder Section */}
          <div className="final-reminder-section">
            <p className="final-reminder-text">
              <strong>Final Reminder</strong>
            </p>
            <p className="final-reminder-text">
              By signing this declaration, you confirm that all information provided is true and accurate. Any misrepresentation may lead to policy rejection or claim denial.
            </p>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default Step8Declaration;
